import axios from "axios";

import { toast } from "@teamprevent/common/toasts";
import {
    SUBMIT_QUESTIONNAIRE,
    submitQuestionnaireSuccess,
    QuestionnaireAction,
    questionnaireLoaded,
    ImmunisationReviewAutoSavePayload
} from "@teamprevent/common/questionnaires/actions";
import { AnsweredQuestionWithId } from "@teamprevent/common/model";
import { loadAndTrack } from "redux-request-loading";
import { Payload } from "@neworbit/redux-helpers";
import {
    SUBMIT_IMMUNISATION_REVIEW_SUCCESS,
    LOAD_IMMUNISATION_REVIEW,
    AUTO_SAVE_IMMUNISATION_REVIEW,
    AUTO_SAVE_IMMUNISATION_REVIEW_SUCCESS,
    LOAD_SAVED_IMMUNISATION_REVIEW
} from "@teamprevent/common/questionnaires/components/immunisationReview/actionTypes";
import { ImmunisationDecision } from "@teamprevent/common/immunisation/models";

import { ThunkAction, ThunkDispatch } from "redux-thunk";

import { ApplicationState } from "../applicationState";
import { AddTaskFormModel } from "../task/model";
import { AddNextTaskFormModel } from "../tasks/models";
import { ImmunisationCreateModel } from "../immunisations/model";

import { ImmunisationReviewApi } from "./ImmunisationReviewApi";
import { ImmunisationReviewResult, FollowUpTaskModel, SaveImmunisationReviewModel } from "./model";

export type ImmunisationReviewAction =
    ({ type: typeof SUBMIT_IMMUNISATION_REVIEW_SUCCESS } & Payload<ImmunisationReviewResult>) |
    ({ type: typeof AUTO_SAVE_IMMUNISATION_REVIEW_SUCCESS } & Payload<ImmunisationReviewAutoSavePayload>) |
    ({ type: typeof LOAD_SAVED_IMMUNISATION_REVIEW } & Payload<SaveImmunisationReviewModel>);

export const submitImmunisationReview = buildSubmitImmunisationReview(new ImmunisationReviewApi(axios));

export function buildSubmitImmunisationReview(immunisationReviewApi: ImmunisationReviewApi) {
    return (
        ohCaseId: string,
        taskId: string,
        questions: AnsweredQuestionWithId[],
        questionnaireKey: string,
        tasks: FollowUpTaskModel[],
        immunisationDecisions: { immunisationTypeId: string; immunisationDecision: ImmunisationDecision }[],
        immunisationRecords: ImmunisationCreateModel[]):
        ThunkAction<Promise<void>, ApplicationState, undefined, ImmunisationReviewAction> => {
        return async (dispatch: ThunkDispatch<ApplicationState, undefined, ImmunisationReviewAction | QuestionnaireAction>) => {
            const result = await loadAndTrack(
                dispatch,
                SUBMIT_QUESTIONNAIRE,
                immunisationReviewApi.submitImmunisationReview(ohCaseId, taskId, questions, tasks, immunisationDecisions, immunisationRecords)
            );
            toast.success("Immunisation review submitted successfully");
            dispatch(submitQuestionnaireSuccess(questionnaireKey));
            dispatch(submitImmunisationReviewSuccess(result));
        };
    };
}

export const loadImmunisationReview = buildLoadImmunisationReview(new ImmunisationReviewApi(axios));

export function buildLoadImmunisationReview(immunisationReviewApi: ImmunisationReviewApi) {
    return (ohCaseId: string, taskId: string, questionnaireKey: string): ThunkAction<Promise<void>, ApplicationState, undefined, ImmunisationReviewAction> => {
        return async (dispatch: ThunkDispatch<ApplicationState, undefined, ImmunisationReviewAction | QuestionnaireAction>) => {
            const response = await loadAndTrack(dispatch, LOAD_IMMUNISATION_REVIEW, immunisationReviewApi.loadImmunisationReview(ohCaseId, taskId));
            if (response) {
                const questions = response.data.questions || [];
                dispatch(questionnaireLoaded({ questions, questionnaireKey }));
            }
        };
    };
}

export const saveImmunisationReview = buildSaveImmunisationReview(new ImmunisationReviewApi(axios));

export function buildSaveImmunisationReview(immunisationReviewApi: ImmunisationReviewApi) {
    return (
        ohCaseId: string,
        taskId: string,
        questions: AnsweredQuestionWithId[],
        questionnaireKey: string,
        tasks: { task: AddTaskFormModel; isValid: boolean }[],
        immunisationTasks: { taskModel: AddNextTaskFormModel; immunisationTypeId: string }[],
        immunisationRecords: { immunisationModel: ImmunisationCreateModel; immunisationTypeId: string }[],
        eTag: string):
        ThunkAction<Promise<void>, ApplicationState, undefined, ImmunisationReviewAction> => {
        return async (dispatch: ThunkDispatch<ApplicationState, undefined, ImmunisationReviewAction | QuestionnaireAction>) => {
            const result = await loadAndTrack(
                dispatch,
                AUTO_SAVE_IMMUNISATION_REVIEW,
                immunisationReviewApi.saveImmunisationReview(ohCaseId, taskId, questions, tasks, immunisationTasks, immunisationRecords, eTag)
            );
            dispatch(saveImmunisationReviewSuccess({
                questionnaireKey,
                eTag: result.eTag
            }));
        };
    };
}

const buildLoadSavedImmunisationReview = (immunisationReviewApi: ImmunisationReviewApi) =>
    (ohCaseId: string, taskId: string, questionnaireKey: string):
        ThunkAction<Promise<SaveImmunisationReviewModel>, ApplicationState, undefined, any> =>
        async (dispatch) => {
            const response = await loadAndTrack(
                dispatch,
                LOAD_SAVED_IMMUNISATION_REVIEW,
                immunisationReviewApi.loadSavedImmunisationReview(ohCaseId, taskId));
            if (response) {
                const questions = response.questionsData?.questions || [];
                dispatch(questionnaireLoaded({ questions, questionnaireKey }));
            }

            return response;
        };

export const loadSavedImmunisationReview = buildLoadSavedImmunisationReview(new ImmunisationReviewApi(axios));

export function submitImmunisationReviewSuccess(payload: ImmunisationReviewResult): ImmunisationReviewAction {
    return {
        type: SUBMIT_IMMUNISATION_REVIEW_SUCCESS,
        payload
    };
}

export function saveImmunisationReviewSuccess(payload: ImmunisationReviewAutoSavePayload): ImmunisationReviewAction {
    return {
        type: AUTO_SAVE_IMMUNISATION_REVIEW_SUCCESS,
        payload
    };
}
